import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BadgeCheck, Clock, AlertCircle, LogOut, CreditCard, Receipt } from "lucide-react";

import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { api, brl, dateBR, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { MemberCard } from "../components/admin/MemberCard";

const statusMap = {
  active: { label: "Ativo", color: "bg-emerald-100 text-emerald-800" },
  overdue: { label: "Mensalidade vencida", color: "bg-rose-100 text-rose-800" },
  pending: { label: "Aguardando pagamento", color: "bg-amber-100 text-amber-800" },
  blocked: { label: "Bloqueado", color: "bg-rose-100 text-rose-800" },
};

export default function MemberDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      setLoading(true);
      const { data } = await api.get("/member/me");
      setData(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  if (!user) return null;

  const me = data?.user || user;
  const paidThisMonth = me.situation === "active";
  const hasAwaiting = data?.payments?.some((p) => p.status === "awaiting");
  const st = statusMap[me.situation] || statusMap.pending;

  async function doLogout() {
    await logout();
    nav("/");
  }

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14" data-testid="member-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-[#166534]">ÁREA DO ASSOCIADO</p>
          <h1 className="font-display mt-2 text-3xl md:text-4xl font-extrabold tracking-tight">
            Olá, {user.nome_completo?.split(" ")[0] || "Associado"}
          </h1>
        </div>
        <Button
          variant="outline"
          onClick={doLogout}
          data-testid="member-logout"
          className="rounded-full"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sair
        </Button>
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 md:col-span-1">
          <p className="text-xs font-semibold text-neutral-500 tracking-wider">Nº DE CADASTRO</p>
          <p
            className="font-display mt-2 text-3xl font-extrabold text-[#166534]"
            data-testid="cadastro-number"
          >
            {user.cadastro_number}
          </p>
          <Badge className={`mt-4 ${st.color} border-0 hover:${st.color}`} data-testid="member-status">
            {st.label}
          </Badge>
        </div>

        <div className="rounded-2xl border border-neutral-200 bg-white p-6 md:col-span-2">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold text-neutral-500 tracking-wider">
                MENSALIDADE · 30 DIAS
              </p>
              <p className="font-display mt-2 text-3xl font-extrabold">{brl(30)}</p>
              {me.next_due_date && (
                <p className="text-sm text-neutral-600 mt-1" data-testid="member-next-due">
                  Próximo vencimento: <strong>{dateBR(me.next_due_date)}</strong>
                  {me.last_payment_at && <> · Último pagamento: {dateBR(me.last_payment_at)}</>}
                </p>
              )}
            </div>
            {paidThisMonth ? (
              <Badge
                className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-0"
                data-testid="payment-status-current"
              >
                <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Em dia
              </Badge>
            ) : (
              <Badge
                className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-0"
                data-testid="payment-status-current"
              >
                <Clock className="w-3.5 h-3.5 mr-1" /> Pendente
              </Badge>
            )}
          </div>

          {hasAwaiting && (
            <div className="mt-5 flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4" data-testid="awaiting-confirmation">
              <Clock className="w-5 h-5 text-amber-700 shrink-0" />
              <p className="text-sm text-amber-900 font-semibold">Aguardando confirmação do pagamento</p>
            </div>
          )}

          {me.status === "blocked" ? (
            <div className="mt-5 flex items-start gap-3 rounded-xl bg-rose-50 border border-rose-200 p-4">
              <AlertCircle className="w-5 h-5 text-rose-700 shrink-0" />
              <p className="text-sm text-rose-900">
                Seu cadastro está bloqueado. Entre em contato pelo WhatsApp.
              </p>
            </div>
          ) : (
            <Link to="/pagamento">
              <Button
                data-testid="go-to-payment"
                className="mt-6 rounded-full bg-[#166534] hover:bg-[#14532d] hover:text-white text-white h-11 px-6"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {paidThisMonth ? "Antecipar mensalidade (+30 dias)" : "Pagar mensalidade"}
              </Button>
            </Link>
          )}
        </div>
      </div>

      {me.status === "active" && me.next_due_date ? (
      <div className="mt-8 rounded-2xl border border-neutral-200 bg-white p-6 md:p-8 grid md:grid-cols-[auto_1fr] gap-6 items-center" data-testid="member-card-section">
        <MemberCard member={me} />
        <div>
          <h2 className="font-display text-xl font-extrabold">Sua carteirinha digital</h2>
          <p className="text-sm text-neutral-600 mt-2 max-w-md">
            Apresente esta carteirinha ao agendar ou comparecer às consultas com profissionais parceiros.
            Você pode mostrar na tela do celular ou imprimir.
          </p>
          <Button onClick={() => window.print()} variant="outline" className="mt-4 rounded-full" data-testid="member-print-card">
            <Printer className="w-4 h-4 mr-2" /> Imprimir / Salvar PDF
          </Button>
        </div>
      </div>
      ) : (
        <div className="mt-8 rounded-2xl border border-dashed border-neutral-300 bg-white p-6 text-sm text-neutral-600" data-testid="card-locked">
          Sua carteirinha digital será liberada após a confirmação da primeira mensalidade.
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-neutral-200 bg-white p-6 md:p-8">
        <div className="flex items-center gap-2 mb-4">
          <Receipt className="w-5 h-5 text-[#166534]" />
          <h2 className="font-display text-xl font-extrabold">Histórico de pagamentos</h2>
        </div>
        {loading ? (
          <p className="text-sm text-neutral-500">Carregando…</p>
        ) : !data?.payments?.length ? (
          <p className="text-sm text-neutral-500" data-testid="no-payments">
            Nenhum pagamento registrado ainda.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead>
                <tr className="text-left text-neutral-500">
                  <th className="py-2 font-medium">Cobre até</th>
                  <th className="py-2 font-medium">Método</th>
                  <th className="py-2 font-medium">Valor</th>
                  <th className="py-2 font-medium">Pago em</th>
                  <th className="py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.payments
                  .slice()
                  .sort((a, b) => ((a.created_at || "") < (b.created_at || "") ? 1 : -1))
                  .map((p) => (
                    <tr key={p.id} className="border-t border-neutral-100">
                      <td className="py-3 font-semibold">{p.covers_until ? dateBR(p.covers_until) : "-"}</td>
                      <td className="py-3 capitalize">{p.method || "-"}</td>
                      <td className="py-3">{brl(p.amount)}</td>
                      <td className="py-3">
                        {p.status === "paid" ? dateBR(p.paid_at) : "-"}
                      </td>
                      <td className="py-3">
                        {p.status === "paid" ? (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-0">
                            Pago
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-0">
                            Aguardando confirmação
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
